import React, { useEffect, useState } from "react";
import OpportunityCard from "@/app/global-components/opportunity-card";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Loader2 } from "lucide-react";
import { routes } from "@/consts";
import { LANG } from "@/types";
import useOffer from "@/hooks/useTender";

const OffersPage: React.FC = () => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const [opportunities, setOpportunities] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const { getOffers } = useOffer();

  useEffect(() => {
    const fetchOffers = async () => {
      try {
        setLoading(true);
        const Offers = await getOffers("Opened");
        if (Offers) {
          setOpportunities(Offers.data);
        } else {
          setOpportunities([]);
        }
      } catch (error) {
        console.error("Error fetching Offers:", error);
        setOpportunities([]);
      } finally {
        setLoading(false);
      }
    };

    fetchOffers();
  }, []);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 30,
      scale: 0.95,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  const headerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  return (
    <section
      id="latestOpportunities"
      className="py-16 bg-gradient-to-br from-gray-50 to-white"
    >
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          variants={headerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t("general.LatestMedicalTenders")}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-6">
            {t("general.AvailableOpportunitiesList")}
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-purple-500 to-blue-500 mx-auto rounded-full"></div>
        </motion.div>

        {/* Loading State */}
        {loading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex justify-center items-center py-16"
          >
            <div className="flex items-center gap-3">
              <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
              <span className="text-lg text-gray-600">
                {t("status.loading")}
              </span>
            </div>
          </motion.div>
        )}

        {/* Opportunities Grid */}
        {!loading && (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 mb-12"
          >
            {opportunities.slice(0, 8).map((opportunity) => (
              <motion.div
                key={opportunity.id}
                variants={itemVariants}
                className="group"
              >
                <div className="h-full">
                  <OpportunityCard opportunity={opportunity} />
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Empty State */}
        {!loading && opportunities.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16"
          >
            <div className="max-w-md mx-auto">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {t("general.noDataMatchingCriteria")}
              </h3>
              <p className="text-gray-600">{t("general.NoDataAvailable")}</p>
            </div>
          </motion.div>
        )}

        {/* View More Button */}
        {!loading && opportunities.length > 6 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Link to={routes.tenders}>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-flex items-center gap-3 bg-gradient-to-r from-purple-500 to-blue-500 text-white px-8 py-4 rounded-full font-semibold hover:shadow-lg transition-all duration-300 shadow-md"
              >
                <span>{t("actions.viewMore")}</span>
                {locale === LANG.EN ? (
                  <ArrowRight className="w-5 h-5" />
                ) : (
                  <ArrowLeft className="w-5 h-5" />
                )}
              </motion.button>
            </Link>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default OffersPage;
